import { ReactNode } from 'react';
import { MdOutlineAddchart } from 'react-icons/md';
import {
    TbBolt,
    TbBuilding,
    TbFilePlus,
    TbLayout,
    TbLayoutGrid,
    TbLock,
    TbRoute,
    TbSchool,
    TbShare,
    TbSparkles,
    TbUser,
    TbUsers,
    TbUsersPlus,
} from 'react-icons/tb';

import {
    WorkspaceFolderGlyph,
    WorkspaceResourceGlyph,
} from '@/shared/components/workspace-resources';
import { UserPlan } from '@/types';

import type { MapToken } from './types';

export const createGeneralIconMaps = (_token: MapToken) => {
    const resourceTypeToIconMap = {
        Personal: <TbUser size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
        Team: <TbUsers size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
        Shared: <TbShare size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
    } as Record<string, ReactNode>;

    const resourceVisibilityToIconMap = {
        Private: <TbLock style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />,
        Workspace: <TbLayoutGrid style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />,
    } as Record<string, ReactNode>;

    const workspaceResourceToIconMap = {
        investment: <WorkspaceResourceGlyph resource='investment' />,
        memo: <WorkspaceResourceGlyph resource='memo' />,
        pipeline: <WorkspaceResourceGlyph resource='pipeline' />,
        team: <WorkspaceResourceGlyph resource='team' />,
        folder: <WorkspaceResourceGlyph resource='folder' />,
        dashboard: <WorkspaceResourceGlyph resource='dashboard' />,
        lifecycle: <WorkspaceResourceGlyph resource='lifecycle' />,
        list: <WorkspaceResourceGlyph resource='list' />,
        report: <WorkspaceResourceGlyph resource='report' />,
        task: <WorkspaceResourceGlyph resource='task' />,
    } as Record<string, ReactNode>;

    const pluralWorkspaceResourceToIconMap = {
        investments: <WorkspaceResourceGlyph resource='investment' />,
        memos: <WorkspaceResourceGlyph resource='memo' />,
        pipelines: <WorkspaceResourceGlyph resource='pipeline' />,
        teams: <WorkspaceResourceGlyph resource='team' />,
        folders: <WorkspaceResourceGlyph resource='folders' />,
        dashboards: <WorkspaceResourceGlyph resource='dashboard' />,
        lifecycles: <WorkspaceResourceGlyph resource='lifecycle' />,
        lists: <WorkspaceResourceGlyph resource='list' />,
        reports: <WorkspaceResourceGlyph resource='report' />,
        tasks: <WorkspaceResourceGlyph resource='task' />,
    } as Record<string, ReactNode>;

    const workspaceResourceToCreationIconMap = {
        investment: <MdOutlineAddchart />,
        memo: <TbFilePlus />,
        pipeline: <TbRoute />,
        team: <TbUsersPlus />,
        folder: <WorkspaceFolderGlyph variant='create' />,
        dashboard: <TbLayout />,
        list: <WorkspaceResourceGlyph resource='list' />,
    } as Record<string, ReactNode>;

    const userPlanToIconMap = {
        [UserPlan.FREE]: <></>,
        [UserPlan.PLUS_ANNUALLY]: (
            <TbSparkles size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />
        ),
        [UserPlan.PLUS_MONTHLY]: (
            <TbSparkles size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />
        ),
        [UserPlan.PRO_ANNUALLY]: <TbBolt size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        [UserPlan.PRO_MONTHLY]: <TbBolt size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        [UserPlan.EDUCATION_ANNUALLY]: (
            <TbSchool size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />
        ),
        [UserPlan.EDUCATION_MONTHLY]: (
            <TbSchool size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />
        ),
        [UserPlan.ENTERPRISE]: (
            <TbBuilding size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />
        ),
        [UserPlan.INACTIVE]: <></>,
    } as Record<UserPlan, ReactNode>;

    return {
        resourceTypeToIconMap,
        resourceVisibilityToIconMap,
        workspaceResourceToIconMap,
        pluralWorkspaceResourceToIconMap,
        workspaceResourceToCreationIconMap,
        userPlanToIconMap,
    };
};
